import { useMemo } from 'react';
import { toCents, cents, showMoneyWithCents } from '../Data/Money';
import { ColDefWithCurrency } from './AgingReport';

interface GridFooterProps<TData> {
    type: string;
    data: TData[];
    columnDefs: ColDefWithCurrency<TData>[];
}

export function GridFooter<TData>({ 
    type, 
    data, 
    columnDefs 
}: GridFooterProps<TData>) {
    const totals = useMemo(() => {
        const result: Record<string, number> = {};
        
        const numericColumns = columnDefs.filter(col => {
            if (col.hide) return false;
            const isCurrency = col.isCurrency;
            const isNumeric = col.type === 'rightAligned';
            return isCurrency || isNumeric;
        });

        numericColumns.forEach(col => {
            if (!col.field) return;
            if ((col as ColDefWithCurrency<TData>).isCurrency) {
                try {
                result[col.field] = data.reduce((acc, row) => {
                const value = (row as any)[col.field as string];
                return acc + toCents(value);
                }, 0); }
                catch{
                    result[col.field] = data.reduce((acc, row) => {
                    let value = (row as any)[col.field as string];
                    value = cents(value);
                    return acc + (value ? toCents(value) : 0);
                }, 0);
                }

            } else if (col.type === 'rightAligned') {
                result[col.field] = data.reduce((acc, row) => {
                    const value = (row as any)[col.field as string];
                    return acc + (value ?? 0);
                }, 0);
            }
        });
        
        return result;
    }, [data, columnDefs]);

    if (Object.keys(totals).length === 0) return null;

    return (
        <div className="grid-footer border-top bg-light p-2">
            <div className="d-flex flex-wrap gap-4">
                {Object.entries(totals).map(([field, total]) => {
                    const colDef = columnDefs.find(col => col.field === field);
                    if (!colDef) return null;

                    const displayValue = (colDef as ColDefWithCurrency<TData>).isCurrency
                    ? showMoneyWithCents(total) 
                    : total.toLocaleString();

                    return (
                        <div key={field} className="d-flex align-items-center">
                            <span className="text-muted me-2">{colDef.headerName}:</span>
                            <span className="fw-bold">{displayValue}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
} 